<script lang="ts">
  import { onMount } from "svelte";
  import Router, { location } from "svelte-spa-router";
  import { session } from "$shared/lib/session-parkito";
  import {
    Card,
    ErrorCloud,
    LeftNavigation,
    LoadingIndicator,
    Toast,
    TopNavigation,
  } from "$components";
  import Login from "$shared/views/Login.svelte";
  import type { NavbarEntry } from "$shared/lib/navbar";
  import SiteView from "./views/Site.svelte";
  import SettingsView from "./views/Settings.svelte";
  import NotFound from "$shared/views/NotFound.svelte";
  import SpotView from "./views/Spot.svelte";
  import ProfileView from "./views/Profile.svelte";
  import SpotSharingView from "./views/SpotSharing.svelte";
  import MessagesView from "./views/Messages.svelte";
  import ContactView from "./views/Contact.svelte";
  import AboutView from "./views/About.svelte";
  import MessageView from "./views/Message.svelte";
  import InvoicesView from "./views/Invoices.svelte";
  import AgreementView from "./views/Agreement.svelte";
  import AcceptUserInviteView from "./views/AcceptUserInvite.svelte";
  import AgreementTermsView from "./views/AgreementTerms.svelte";
  import PaymentMethodView from "./views/PaymentMethod.svelte";
  import DeliveryMethodView from "./views/DeliveryMethod.svelte";
  import QueueView from "./views/Queue.svelte";
  import ApplicantDetailsView from "./views/ApplicantDetails.svelte";
  import QueueApplicantCreateDebtor from "./views/QueueApplicantCreateDebtor.svelte";
  import * as Sentry from "@sentry/svelte";
  import "../../../node_modules/easymde/dist/easymde.min.css";
  import { withApi } from "$shared/lib/api-parkito";
  import type { MyPagesStart } from "../../apis/parkito";
  import wrap from "svelte-spa-router/wrap";
  import { writable } from "svelte/store";
  import AcceptSpotShareView from "./views/AcceptSpotShare.svelte";
  import SharedSpotView from "./views/SharedSpot.svelte";
  import "../../globals.css";
  import CheckoutSuccess from "./views/Store/CheckoutSuccess.svelte";
  import PermitCheckout from "./views/Store/PermitCheckout.svelte";
  import ReservedQueueCheckout from "./views/Store/ReservedQueueCheckout.svelte";
  import { svelte5Comp } from "$shared/lib/router.ts";
  import { MyPagesStartModel } from "$shared/models/mod.ts";

  const getMyPagesData = async () => {
    isLoading = true;
    const res = await withApi((api) => {
      return api.startMyPages();
    });

    if (res.isOk()) {
      myPagesData.set(res.value);
    } else {
      error = JSON.stringify(res.error);
      Toast.error("Kunde inte hämta data för mina sidor");
    }
    isLoading = false;
  };

  const navbarEntries: NavbarEntry[] = [
    {
      label: "Mina sidor",
      hash: "#/",
    },
  ];
  let isLoadedFromApp = false;
  let isLoading: boolean = false;
  let error: string | undefined;
  let myPagesData = writable<MyPagesStart>();

  const routes = {
    "/": wrap({
      component: svelte5Comp(SiteView),
      props: { params: { id: undefined } },
    }),
    "/site/:id": wrap({
      component: svelte5Comp(SiteView),
    }),
    "/site/:id/spot/:spotid": wrap({
      component: svelte5Comp(SpotView),
    }),
    "/site/:id/shared-spot/:spotid": wrap({
      component: svelte5Comp(SharedSpotView),
    }),
    "/site/:siteId/queue/:queueId": wrap({
      component: svelte5Comp(QueueView),
      props: { myPagesData },
    }),
    "/confirm_queue_info/:applicantId": wrap({
      component: svelte5Comp(QueueApplicantCreateDebtor),
      props: { myPagesData, refreshMyPagesData: getMyPagesData },
    }),
    "/site/:id/spot/:spotid/share/:spotsharingid": wrap({
      component: svelte5Comp(SpotSharingView),
    }),
    "/applicant/:applicantId": wrap({
      component: svelte5Comp(ApplicantDetailsView),
      props: { myPagesData },
    }),
    "/accept-spot-share/:id": AcceptSpotShareView,
    "/profile/:id": wrap({
      component: svelte5Comp(ProfileView),
      props: { myPagesData },
    }),
    "/profile/:id/delivery-method": wrap({
      component: svelte5Comp(DeliveryMethodView),
      props: { myPagesData },
    }),
    "/profile/:id/payment-method": wrap({
      component: svelte5Comp(PaymentMethodView),
      props: { myPagesData },
    }),
    "/profile/:id/invoices": wrap({
      component: svelte5Comp(InvoicesView),
      props: { myPagesData },
    }),
    "/agreements/:id": wrap({
      component: svelte5Comp(AgreementView),
      props: { myPagesData },
    }),
    "/agreements/:id/terms": AgreementTermsView,
    "/messages": MessagesView,
    "/messages/:id": MessageView,
    "/contact": ContactView,
    "/about": AboutView,
    "/settings": SettingsView,
    "/store/:site_id/queue/checkout/:queue_id": ReservedQueueCheckout,
    "/store/:site_id/permit/checkout/customer_info": PermitCheckout,
    "/store/:site_id/permit/checkout/success": CheckoutSuccess,
    "/invite/:invite_code": wrap({
      component: svelte5Comp(AcceptUserInviteView),
    }),
    "*": NotFound,
  };

  onMount(() => {
    isLoadedFromApp =
      new URLSearchParams(window.location.search).get("app") === "true";

    if (!import.meta.env.DEV) {
      Sentry.init({
        dsn:
          "https://e9bf9a582d80b21e76503254a7173548@o4506580301185024.ingest.sentry.io/4506654754734080",
        tracePropagationTargets: [
          "localhost",
          /^https:\/\/minasidor\.parkando\.se\/api/,
        ],
        integrations: [Sentry.browserTracingIntegration()],
        // Performance Monitoring
        tracesSampleRate: 1.0, //  Capture 100% of the transactions
      });
    }
  });

  $: {
    if ($session) {
      getMyPagesData();

      Sentry.setUser({
        id: $session.user?.id,
      });
    }
  }

  import { myPages } from "./stores/myPagesData.svelte.ts";
  session.subscribe((ses) => {
    if (ses) {
      $myPages.load();
    }
  });
</script>

{#if $session}
  {#if !isLoadedFromApp}
    <TopNavigation masqUrl="/404.html" {navbarEntries} env="mypages" />
  {/if}
  <div
    id="page"
    class="
      min-h-fit
      m-auto max-w-[1280px] px-[8px] pb-[24px] flex flex-col h-screen justify-between {isLoadedFromApp
      ? 'pt-[8px]'
      : 'pt-[74px]'}
    "
  >
    <Card
      customClass="max-mobile:bg-transparent max-mobile:border-none max-mobile:shadow-none"
    >
      <div class="flex">
        {#if isLoading}
          <LoadingIndicator visible={true} customClass="py-[96px]" />
        {:else if $myPagesData}
          <LeftNavigation
            data={MyPagesStartModel.fromLegacyApi($myPagesData)}
          />
          <div class:max-mobile:hidden={$location === "/"} class="w-full">
            <Router {routes} />
          </div>
        {:else}
          <div class="mx-auto pb-[48px]">
            <ErrorCloud
              altMessage="page-not-available"
              title="Något gick snett"
              text="Ett fel uppstod vid inladdning, försök snart igen. Skulle
              problemet kvarstå, vänligen kontakta support."
            />
          </div>
        {/if}
      </div>
    </Card>
    <p
      class="text-[12px] text-[#9FB1B2] w-full text-center py-[32px] max-mobile:hidden"
    >
      © Parkdando AB {new Date().getFullYear()}
    </p>
  </div>
{:else}
  <Login env="mypages" />
{/if}
